import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import {of as observableOf,  Observable } from 'rxjs';
import {catchError} from 'rxjs/operators';
import { ErrorResponse, SuccessResponse } from '@shared/services/api/response';
import { IRegistration } from '@shared/services/api/legacy/landingpages-service/registrationpage-model';
import { ActivityFilter, IActivityLogs, IAdvancedSortSettings, IDeclineInvitation, IInvitationDetail, SyncInvitationsToCrm, IAddContact } from './invitations-model';

@Injectable()
export class InvitationsService {
    constructor(
        private http: HttpClient
    ) { }

    private static get URL(): string { return `${environment.dashboardApi.baseUrl}${environment.dashboardApi.prefix}`; }
    private static readonly ENDPOINT = 'events/{eventId}/invitations';

    /** GET */
    public getInvitation(eventId: string, invitationId: string): Observable<SuccessResponse<IInvitationDetail> | ErrorResponse>  {
        return this.http
            .get<SuccessResponse<IInvitationDetail>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/' + invitationId
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }


    /** PUT */
    public confirmInvitation(eventId: string, data: IRegistration): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/' + data?.invitationId +
                '/confirmations',
                data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public editConfirmInvitation(eventId: string, data: IRegistration): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/' + data?.invitationId +
                '/updateregistrations',
                data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public declineInvitation(eventId: string, data: IDeclineInvitation): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .put<SuccessResponse<boolean>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/declines',
                data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public syncInvitationsToCrm(eventId: string, data: SyncInvitationsToCrm): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/synctocrms',
                data
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public syncInvitationsToInviteDesk(eventId: string): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/synctoinvitedesk',
                {}
            ).pipe(
            catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }

    public getActivityLogsByInvitation(eventId: string, invitationId: string): Observable<SuccessResponse<IActivityLogs> | ErrorResponse> {
        return this.http
            .get<SuccessResponse<IActivityLogs>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/' + invitationId +
                '/activities')
            .pipe(
            catchError(error => observableOf(new ErrorResponse(error?.error?.messages))));
    }

    public getActivityLogs(eventId: string, filter: ActivityFilter): Observable<SuccessResponse<IActivityLogs> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<IActivityLogs>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/activities', filter)
            .pipe(
            catchError(error => observableOf(new ErrorResponse(error.error))));
    }

    public advancedSort(eventId: string, model: IAdvancedSortSettings): Observable<SuccessResponse<boolean> | ErrorResponse> {
        return this.http
            .post<SuccessResponse<boolean>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/rankings/advancedsort', model).pipe(
            catchError(error => observableOf(new ErrorResponse(error?.error?.messages))));
    }

    /** POST */
    public addContact(contact: IAddContact, eventId: string):
    Observable<SuccessResponse<boolean> | ErrorResponse>{
        return this.http
            .post<SuccessResponse<boolean>>(
                InvitationsService.URL +
                InvitationsService.ENDPOINT.replace('{eventId}', eventId) +
                '/contact', contact
            )
            .pipe(catchError(x => observableOf(new ErrorResponse(x?.error?.messages))));
    }
}
